import React from "react";
import {
  Avatar,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useNavigate, useLocation } from "react-router-dom";
import { firebaseHelper, useDesignFirm, useWorkspaceContext } from "ui";
import { AuthContext } from "../auth/Auth";
import { useLoginState } from "../studioxr/auth/LoginState";
import { roleLabel } from "../workspace/useWorkspaceMenu";
import {
  workspaceColor,
  workspaceInitials,
} from "../workspace/workspaceAvatar";
import { navIcons, navRowSx, navRowIconSx, navRowTextProps } from "./navIcons";

interface TeamSidebarNavProps {
  collapsed: boolean;
  /** Slides back to the main nav; stays on the current page. */
  onBack: () => void;
  onBeforeNavigate?: () => boolean;
  onNavigate?: () => void;
}

const TEAM_PAGES = [
  { id: "members", label: "Members", icon: navIcons.team },
  { id: "billing", label: "Billing", icon: navIcons.billing, adminOnly: true },
  { id: "credits", label: "Credits", icon: navIcons.credits },
  {
    id: "settings",
    label: "Settings",
    icon: navIcons.settings,
    adminOnly: true,
  },
];

/**
 * Team drill-down (Team Page v3): replaces the main nav while the Team row
 * is open. Back returns to the main nav; each row is a /home/team page.
 */
export const TeamSidebarNav = ({
  collapsed,
  onBack,
  onBeforeNavigate,
  onNavigate,
}: TeamSidebarNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = React.useContext(AuthContext);
  const permissions = useLoginState((s) => s.permissions);
  const ctx = useWorkspaceContext(currentUser?.uid ?? null, permissions);
  const firmId = ctx.active.kind === "team" ? ctx.active.firmId : null;
  // The live firm doc, so a rename shows here straight away.
  const { firm } = useDesignFirm(firebaseHelper.db, firmId);
  // A firm doc without a name reads "Team" rather than crashing the sidebar.
  const teamName: string | null =
    firm?.id === firmId ? firm.name?.trim() || "Team" : null;
  const pages = TEAM_PAGES.filter((p) => !p.adminOnly || ctx.isAdmin);
  // /home/team (no page) redirects to members, so treat it as members.
  const segment =
    location.pathname.match(/^\/home\/team\/?([^/]*)/)?.[1] || "members";

  const go = (href: string) => {
    if (onBeforeNavigate && !onBeforeNavigate()) return;
    navigate(href);
    onNavigate?.();
  };

  const withTooltip = (title: string, node: React.ReactElement) =>
    collapsed ? (
      <Tooltip key={title} title={title} placement="right">
        {node}
      </Tooltip>
    ) : (
      node
    );

  return (
    <>
      {withTooltip(
        "Back",
        <ListItemButton
          disableRipple
          onClick={onBack}
          data-testid="team-nav-back"
          sx={{
            ...navRowSx({ collapsed, height: 36 }),
            // ListItemButton grows by default; in this column it must not.
            flexGrow: 0,
            alignSelf: collapsed ? "stretch" : "flex-start",
            color: "text.secondary",
            "&:hover": { backgroundColor: "#F4F4F5", color: "#1a1a1a" },
          }}
        >
          <ChevronLeftIcon
            sx={{
              fontSize: 18,
              ml: collapsed ? 0 : "-4px",
              mr: collapsed ? 0 : "4px",
            }}
          />
          {!collapsed && (
            <Typography
              sx={{ fontSize: "13px", fontWeight: 600, color: "inherit" }}
            >
              Back
            </Typography>
          )}
        </ListItemButton>,
      )}

      {withTooltip(
        teamName ?? "Team",
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={collapsed ? "center" : "flex-start"}
          spacing={1.25}
          sx={{ px: collapsed ? 0 : 1.25, py: 1.5 }}
        >
          {teamName === null ? (
            <Skeleton
              variant="rounded"
              width={32}
              height={32}
              sx={{ borderRadius: "8px", flexShrink: 0 }}
            />
          ) : (
            <Avatar
              variant="rounded"
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: workspaceColor(ctx.active),
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {workspaceInitials(teamName)}
            </Avatar>
          )}
          {!collapsed && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                title={teamName ?? undefined}
                sx={{ fontWeight: 700, fontSize: "14px", color: "#1a1a1a" }}
              >
                {teamName ?? <Skeleton width={110} />}
              </Typography>
              {ctx.teamRole && (
                <Typography
                  noWrap
                  sx={{ fontSize: "12px", color: "text.secondary" }}
                >
                  {roleLabel(ctx.teamRole)}
                </Typography>
              )}
            </Box>
          )}
        </Stack>,
      )}

      <List disablePadding sx={{ flexGrow: 1 }}>
        {pages.map((page) => {
          const isActive = segment === page.id;
          return withTooltip(
            page.label,
            <ListItemButton
              key={page.id}
              disableRipple
              selected={isActive}
              onClick={() => go(`/home/team/${page.id}`)}
              data-testid={`team-nav-${page.id}`}
              sx={navRowSx({ collapsed })}
            >
              <ListItemIcon sx={navRowIconSx(isActive, collapsed)}>
                {page.icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={page.label}
                  primaryTypographyProps={navRowTextProps}
                />
              )}
            </ListItemButton>,
          );
        })}
      </List>
    </>
  );
};
